import { forwardRef, useContext, useEffect } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import ErrorIcon from '@/assets/icons/common/error.svg';
import X from '@/assets/icons/common/X.svg';
import { useScrollIntoViewOnChange } from '@/hooks/useScrollIntoToView';
import { useIsMobile } from '@/hooks/useIsMobile';

const AUTO_DISMISS_MS = 5000;

export const Error = () => {
  const {
    error,
    template,
    actions: { setError },
  } = useContext(CheckoutContext);

  const isMobile = useIsMobile();

  const { ref } = useScrollIntoViewOnChange(error);

  const onClose = () => {
    setError(null);
  };

  // Auto-dismiss 5s after a message appears — desktop and mobile alike. Re-armed
  // on every new message and torn down on manual close / unmount so it never
  // fires stale.
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [error, setError]);

  if (!error) return null;

  // `errorFollowsContent` templates position this banner themselves — an
  // overlay anchored above the Footer (see `Payment`) — so it must NOT pin
  // itself to the viewport bottom on top of that.
  if (isMobile && !template?.features.errorFollowsContent) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-[2147483647] flex justify-center px-3 pb-12 pointer-events-none">
        <div className="w-full max-w-md pointer-events-auto">
          <ErrorView ref={ref} message={error} onClose={onClose} />
        </div>
      </div>
    );
  }

  return <ErrorView ref={ref} message={error} onClose={onClose} />;
};

interface ErrorViewProps {
  message: string;
  onClose?: () => void;
}

export const ErrorView = forwardRef<HTMLDivElement, ErrorViewProps>(({ message, onClose }, ref) => (
  <div
    ref={ref}
    data-testid="error-inline"
    role="alert"
    className="relative animate-attention w-full bg-white border border-base-border rounded-lg px-4 py-3 shadow-lg checkout-toast-enter"
  >
    {onClose && (
      <img
        data-testid="error-close-button"
        src={X}
        className="absolute top-2 right-2 w-3.5 h-3.5 cursor-pointer opacity-70"
        onClick={onClose}
      />
    )}
    <div className="flex items-start gap-3 pr-5">
      <img data-testid="error-icon" src={ErrorIcon} className="w-5 h-5 shrink-0 mt-0.5" />
      <div data-testid="error-message" className="text-sm text-base-foreground leading-snug break-words text-left">
        {message}
      </div>
    </div>
  </div>
));

ErrorView.displayName = 'ErrorView';
